import React, { useCallback, useEffect, useState } from "react";
import { ArrowLeft, RefreshCw, Search, ClipboardList, Shield, FileDown } from "lucide-react";
import { Card, Button, Input, Table, Pagination, Badge, Tooltip } from "./UI";
import { apiFetch, useApp } from "../../context/AppContext";

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Escape a single CSV cell value */
function csvCell(value) {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

/** Convert an array of objects to a CSV string */
function buildCsv(rows) {
  const headers = ["Recipient", "Subject", "Status", "Error Message", "Sent At"];
  const lines = [headers.map(csvCell).join(",")];
  for (const row of rows) {
    lines.push(
      [
        row.to_email,
        row.subject,
        row.status,
        row.error_message || "",
        row.created_at
          ? new Date(row.created_at).toLocaleString(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : "",
      ]
        .map(csvCell)
        .join(",")
    );
  }
  return lines.join("\r\n");
}

/** Trigger browser download of text content as a file */
function downloadFile(content, filename, mimeType) {
  // UTF-8 BOM so Excel opens it correctly
  const bom = "\uFEFF";
  const blob = new Blob([bom + content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function EmailActivityLogs({ apiBase, isPlatform }) {
  const { showNotification } = useApp();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const pageSize = 10;

  // ── Fetch paginated logs ─────────────────────────────────────────────────────
  const fetchLogs = useCallback(
    async (targetPage = page, searchTerm = search) => {
      setLoading(true);
      try {
        const query = new URLSearchParams({
          page: String(targetPage),
          page_size: String(pageSize),
        });
        if (searchTerm) query.append("search", searchTerm);
        const res = await apiFetch(`${apiBase}/smtp/logs?${query.toString()}`);
        if (!res.ok) throw new Error("Failed to fetch email logs");
        const data = await res.json();
        setLogs(data.items || []);
        setTotal(data.total || 0);
        setPage(data.page || 1);
        setPages(data.pages || 1);
      } catch (e) {
        showNotification(e.message || "Failed to fetch email logs", "error");
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiBase, showNotification]
  );

  useEffect(() => {
    fetchLogs(1, search);
  }, [fetchLogs, search]);

  // ── Export all logs to Excel-compatible CSV ──────────────────────────────────
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      // Fetch ALL logs (up to 10 000) with current search filter
      const query = new URLSearchParams({ page: "1", page_size: "10000" });
      if (search) query.append("search", search);
      const res = await apiFetch(`${apiBase}/smtp/logs?${query.toString()}`);
      if (!res.ok) throw new Error("Failed to fetch logs for export");
      const data = await res.json();
      const allItems = data.items || [];

      if (allItems.length === 0) {
        showNotification("No logs available to export", "warning");
        return;
      }

      const csv = buildCsv(allItems);
      const label = isPlatform ? "platform" : "tenant";
      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .slice(0, 19);
      downloadFile(csv, `email-logs-${label}-${timestamp}.csv`, "text/csv;charset=utf-8");
      showNotification(`Exported ${allItems.length} log entries to Excel`, "success");
    } catch (e) {
      showNotification(e.message || "Export failed", "error");
    } finally {
      setExporting(false);
    }
  };

  // ── Columns ──────────────────────────────────────────────────────────────────
  const columns = [
    {
      key: "_sr",
      label: "S.No",
      render: (_val, _row, index) => (
        <span className="text-slate-400 font-medium text-xs">
          {(page - 1) * pageSize + index + 1}
        </span>
      ),
    },
    {
      key: "to_email",
      label: "Recipient",
      render: (val) => (
        <span className="font-medium text-blue-600 break-all">{val}</span>
      ),
    },
    {
      key: "subject",
      label: "Subject",
      render: (val) => <span className="text-slate-600">{val}</span>,
    },
    {
      key: "status",
      label: "Status",
      render: (val, row) => {
        if (val === "success") {
          return <Badge variant="success">Success</Badge>;
        }
        return (
          <Tooltip content={row.error_message || "SMTP error occurred"}>
            <span className="cursor-help">
              <Badge variant="danger">Failed</Badge>
            </span>
          </Tooltip>
        );
      },
    },
    {
      key: "created_at",
      label: "Sent At",
      render: (val) => {
        try {
          return new Date(val).toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
          });
        } catch {
          return val;
        }
      },
    },
  ];

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="p-1 space-y-6 min-h-screen">
      {/* ── HEADER ── */}
      <div data-tour-id="tour-email-logs-header" className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        {/* Left — title */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-slate-700 transition"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-md flex-shrink-0">
            <ClipboardList size={18} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Email Activities Logs</h1>
            <p className="text-sm text-slate-400 flex items-center gap-1 mt-0.5">
              <Shield size={11} />
              {isPlatform
                ? "Platform-wide Outgoing Email Logs"
                : "Organization Outgoing Email Logs"}
            </p>
          </div>
        </div>

        {/* Right — action buttons */}
        <div className="flex items-center gap-2">
          <Button
            data-tour-id="tour-email-logs-refresh"
            variant="secondary"
            onClick={() => fetchLogs(page, search)}
            loading={loading}
            leftIcon={<RefreshCw size={14} />}
          >
            Refresh Logs
          </Button>
        </div>
      </div>

      {/* ── TABLE CARD ── */}
      <Card className="animate-fade-in">
        {/* Search + Excel icon row */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div data-tour-id="tour-email-logs-search" className="flex items-center gap-2 max-w-md w-full">
            <Input
              placeholder="Search logs by recipient or subject..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftElement={<Search size={14} className="text-slate-400" />}
              className="flex-1"
            />
            {/* Excel export icon */}
            <Tooltip content={exporting ? "Exporting…" : "Export all logs to Excel"}>
              <button
                data-tour-id="tour-email-logs-export"
                onClick={handleExportExcel}
                disabled={exporting || loading}
                className={`
                  flex-shrink-0 w-9 h-9 rounded-lg border flex items-center justify-center transition-all
                  ${exporting || loading
                    ? "bg-emerald-50 text-emerald-300 border-emerald-100 cursor-not-allowed"
                    : "bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100 hover:text-emerald-700 hover:border-emerald-300 active:scale-95"}
                `}
              >
                {exporting
                  ? <RefreshCw size={15} className="animate-spin" />
                  : <FileDown size={15} />}
              </button>
            </Tooltip>
          </div>
          {total > 0 && (
            <span className="text-xs text-slate-400 whitespace-nowrap">
              {total} total log{total !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {loading && logs.length === 0 ? (
          <div className="animate-pulse space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-10 bg-slate-50 rounded-lg" />
            ))}
          </div>
        ) : (
          <>
            <div data-tour-id="tour-email-logs-table">
            <Table
              columns={columns}
              data={logs}
              emptyMessage="No email log entries recorded yet."
            />
            </div>
            <Pagination
              page={page}
              pages={pages}
              total={total}
              pageSize={pageSize}
              onPageChange={(p) => fetchLogs(p, search)}
              itemLabel="logs"
            />
          </>
        )}
      </Card>
    </div>
  );
}
